<template>
  <div>
    <v-dialog :value="opened" fullscreen persistent>
      <the-loading-overlay :value="loading" />

      <v-card>
        <v-card-title class="title" justify-center>
          Bulk Upload Processor Edit
          <v-spacer />
          <v-btn
            outline
            color="secondary"
            class="text-none"
            @click="$emit('update:opened', false)"
          >
            Close
          </v-btn>

          <v-btn
            variant="contained"
            color="primary"
            class="font-weight-bold text-none"
            @click="split"
            :loading="loading"
          >
            Done
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-layout class="document-container">
            <v-layout column>
              <v-alert
                v-if="isSelectionModeOn"
                :value="isSelectionModeOn"
                outline
                type="info"
                class="ma-0 selection-banner py-2"
              >
                <v-layout align-center justify-space-between>
                  <span class="body-1">
                    Click on the thumbnails to select pages for
                    <b>{{ documents[selectedDocIndex].name }}</b> document.
                  </span>
                  <v-btn
                    color="primary"
                    class="text-none pa-0 ma-0"
                    @click="selectedDocIndex = null"
                  >
                    <b class="white--text">Done</b>
                  </v-btn>
                </v-layout>
              </v-alert>

              <v-layout wrap align-center justify-center class="pdf-preview">
                <div v-if="pdfLoading">
                  <v-progress-circular
                    indeterminate
                    size="50"
                    class="primary--text ma-5"
                  />
                </div>

                <div
                  v-else
                  class="thumbnail-container"
                  v-for="t in filteredThumbnails"
                  :key="t.id"
                >
                  <div class="body-1">
                    <span>Page {{ t.page }}</span>

                    <span
                      class="flex-shrink-0"
                      v-if="
                        documentsByPage[t.page] &&
                          documentsByPage[t.page].length > 0
                      "
                    >
                      <v-chip
                        small
                        v-for="(doc, index) in documentsByPage[t.page]"
                        :key="index"
                        class="ma-1"
                        :color="
                          selectedPages.includes(t.page) ? 'primary' : 'grey'
                        "
                        text-color="white"
                      >
                        {{ doc.name }}
                      </v-chip>
                    </span>

                    <v-chip color="error" text-color="white" small v-else
                      >Unclassified</v-chip
                    >
                  </div>

                  <img
                    :src="t.thumbnail"
                    class="thumbnail"
                    @click="isSelectionModeOn ? toggleSelect(t.page) : null"
                    :class="{
                      selected: selectedPages.includes(t.page),
                      selectable: isSelectionModeOn,
                    }"
                    :draggable="false"
                  />
                </div>
              </v-layout>
            </v-layout>

            <div class="sidebar text-xs-left">
              <v-layout v-if="parentDocument" column class="">
                <v-layout
                  v-for="(doc, index) in documents"
                  :key="index"
                  column
                  class="question-container"
                  @click="selectedDocIndex = index"
                  :class="{ selected: selectedDocIndex === index }"
                >
                  <v-layout align-center>
                    <h2 style="flex: auto">{{ doc.name }}</h2>
                    <v-btn flat icon color="red" @click.stop="removeDoc(index)">
                      <v-icon>delete_outline</v-icon>
                    </v-btn>
                  </v-layout>

                  <div class="py-1">
                    <span class="body-1">Differentiator</span>
                    <v-text-field
                      label="Differentiator"
                      class="mt-1 default-input differentiator-input"
                      solo
                      hide-details
                      placeholder="Name of employer, company or payer, etc."
                      v-model="doc.differentiator"
                    />
                  </div>

                  <div class="mt-1 py-1">
                    <span class="body-1">Page ranges</span>
                    <v-text-field
                      class="mt-1 default-input page-input"
                      placeholder="Ranges"
                      solo
                      hide-details
                      v-model="documents[index].pageRanges"
                      @focus="selectedDocIndex = index"
                    />
                  </div>
                </v-layout>
              </v-layout>

              <v-divider class="mb-4"></v-divider>

              <v-layout
                class="mb-5 px-3"
                column
                v-if="showAddNewDocumentForm === false"
              >
                <v-btn
                  class="ma-0 text-none"
                  color="primary"
                  @click="showAddNewDocumentForm = true"
                >
                  <v-icon>add</v-icon>
                  Add document
                </v-btn>
              </v-layout>

              <v-layout class="mb-5 px-3" column v-else>
                <span class="body-1 pb-1">Enter document name:</span>

                <v-combobox
                  :items="suggestions"
                  solo
                  flat
                  hide-details
                  class="default-input document-name-input mb-4"
                  placeholder="e.g. W-2"
                  ref="newDocumentNameInput"
                  :search-input.sync="newDocumentName"
                />
                <v-layout>
                  <v-btn
                    outline
                    class="text-none ma-0"
                    color="grey"
                    @click="showAddNewDocumentForm = false"
                    block
                  >
                    Cancel
                  </v-btn>

                  <v-btn
                    contained
                    class="text-none ma-0 ml-2"
                    color="primary"
                    block
                    @click="addNewDocument"
                    :disabled="isEmpty(newDocumentName)"
                  >
                    <v-icon>add</v-icon>
                    Add
                  </v-btn>
                </v-layout>
              </v-layout>

              <!-- Needed because of Intercom bubble -->
              <v-layout class="mb-4"></v-layout>
            </div>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generatePdfThumbnails } from '@/utils/pdf';
import { PDFDocument } from 'pdf-lib';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import fromPairs from 'lodash/fromPairs';
import flatMap from 'lodash/flatMap';
import API from '@/api';
import rangeParser from 'parse-numeric-range';
import mapValues from 'lodash/mapValues';
import TheLoadingOverlay from '@/components/TheLoadingOverlay';
import UtilsMixins from '@/mixins/UtilsMixins';
// import posthog from 'posthog-js';

export default {
  name: 'NewDocumentSplitterDialog',

  mixins: [UtilsMixins],

  components: {
    TheLoadingOverlay,
  },

  props: {
    opened: {
      type: Boolean,
      default: false,
    },

    currentCompany: {
      type: Object,
    },

    parentDocument: {
      type: Object,
    },

    childDocuments: {
      type: Array,
    },

    allUploadQuestions: {
      type: Array,
    },

    pagesToShow: {
      type: Array,
    },
  },

  data() {
    return {
      loading: false,
      pdfLoading: false,
      pdfData: '',
      pdfDoc: null,
      thumbnails: [],

      documents: [],
      selectedDocIndex: null,

      showAddNewDocumentForm: false,
      newDocumentName: '',
    };
  },

  methods: {
    isEmpty,

    async loadThumbnails() {
      this.thumbnails = await generatePdfThumbnails(this.pdfData, 1000);
    },

    async split() {
      try {
        this.loading = true;

        await API.extractedDocument.splitDocuments({
          companyId: this.currentCompany.id,
          extractedDocumentId: this.parentDocument.id,
          documents: this.documents.map(doc => {
            return {
              ...doc,
              pages: rangeParser(doc.pageRanges),
            };
          }),
        });

        // posthog.capture('split bulk document', {
        //   company_id: this.currentCompany.id,
        //   document_count: this.documents.length,
        //   page_count: this.pdfDoc.getPageCount(),
        // });
      } finally {
        this.loading = false;
        this.$emit('update:opened', false);
        this.$emit('refresh-questionnaire');
      }
    },

    removeDoc(index) {
      this.documents = this.documents.filter((_, i) => i !== index);
      this.selectedDocIndex = null;
    },

    addNewDocument() {
      const form = this.newDocumentName.split(/ \(|\)/).filter(Boolean);
      this.documents.push({
        name: form[0],
        pageRanges: '',
        questionId: this.findQuestionIdByOption(this.newDocumentName),
        differentiator: form[1] || '',
      });

      this.selectedDocIndex = this.documents.length - 1;
      this.showAddNewDocumentForm = false;
      this.newDocumentName = '';
    },

    reset() {
      this.thumbnails = [];
      this.documents = [];
      this.showAddNewDocumentForm = false;
      this.newDocumentName = '';
      this.selectedDocIndex = null;
    },

    toggleSelect(page) {
      let newSelectedPages = [];
      if (this.selectedPages.includes(page)) {
        newSelectedPages = this.selectedPages.filter(p => p !== page);
      } else {
        newSelectedPages = [...this.selectedPages, page];
      }

      this.$set(this.documents, this.selectedDocIndex, {
        ...this.documents[this.selectedDocIndex],
        pageRanges: this.convertToRanges(newSelectedPages).join(', '),
      });
    },

    clearSelections() {
      this.selectedPages = [];
    },

    getQuestionIdBySource(source) {
      const question = this.allUploadQuestions.find(q => {
        const uploadPath = this.currentCompany.upload_link.replace(
          this.currentCompany.file_storage_base_url,
          '',
        );
        const answer = source.startsWith(uploadPath)
          ? source.replace(uploadPath + '/', '')
          : source;

        return q.answer && q.answer.split('&!&').includes(answer);
      });

      return question ? question.id : null;
    },

    findQuestionIdByOption(option) {
      const question = this.allUploadQuestions.find(
        q => q.options && q.options.includes(option),
      );

      return question ? question.id : null;
    },
  },

  computed: {
    selectedPages() {
      if (this.selectedDocIndex === null) {
        return [];
      }

      const pages = rangeParser(
        this.documents[this.selectedDocIndex].pageRanges,
      );
      return pages || [];
    },

    documentsByPage() {
      const pageDocumentPairs = flatMap(this.documents, doc => {
        return rangeParser(doc.pageRanges).map(page => [page, doc]);
      });

      // Group by page
      const documentsByPage = pageDocumentPairs.reduce((acc, [page, doc]) => {
        if (!acc[page]) {
          acc[page] = [];
        }
        acc[page].push(doc);
        return acc;
      }, {});

      return documentsByPage;
    },

    isSelectionModeOn() {
      return this.selectedDocIndex !== null;
    },

    filteredThumbnails() {
      let thumbnails;
      if (this.pagesToShow) {
        thumbnails = this.thumbnails.filter(t =>
          this.pagesToShow.includes(t.page),
        );
      } else {
        thumbnails = this.thumbnails;
      }

      return thumbnails.sort((a, b) => a.page - b.page);
    },

    suggestions() {
      return uniq(
        this.allUploadQuestions.flatMap(question => {
          return question.options || [];
        }),
      );
    },
  },

  watch: {
    opened: {
      async handler(value) {
        if (value) {
          try {
            this.reset();

            this.documents = this.childDocuments
              .sort((a, b) => {
                const aFirstPage =
                  a.parent_pages && a.parent_pages[0] ? a.parent_pages[0] : 0;
                const bFirstPage =
                  b.parent_pages && b.parent_pages[0] ? b.parent_pages[0] : 0;

                return aFirstPage - bFirstPage;
              })
              .map(doc => {
                return {
                  name: doc.name,
                  differentiator: doc.differentiator,
                  pageRanges: this.convertToRanges(doc.parent_pages || []).join(
                    ', ',
                  ),
                  questionId: this.getQuestionIdBySource(doc.source),
                };
              });

            this.pdfLoading = true;
            const response = await API.integration.downloadDocument({
              companyId: this.currentCompany.id,
              link: this.parentDocument.source,
            });

            const pdfSrc = response.data.document.data.link;
            const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
            this.pdfDoc = await PDFDocument.load(pdfBytes);
            this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });

            await this.loadThumbnails();
          } finally {
            this.pdfLoading = false;
          }
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.title {
  font-weight: 700;
  padding-top: 0px;
  padding-bottom: 0px;
  height: 60px;
  border-bottom: 1px solid #ccc;
}

.body-1 {
  font-size: 16px !important;
}

.document-container {
  overflow: hidden;
  min-height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
  background-color: #f0f0f0;
}

.selection-banner {
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.25);
}

.pdf-preview {
  flex: auto;
  overflow-y: auto;
}

.sidebar {
  min-width: 350px;
  max-width: 350px;
  border-left: 1px solid #ddd;
  overflow-y: auto;
}

.question-container {
  border-top: 1px solid #ddd;
  padding: 16px;

  &.selected {
    background-color: #e9f4fd;
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
  }
}

.page-input,
.differentiator-input,
.document-name-input {
  border: 1px solid #bbb;
  border-radius: 4px;
  overflow: hidden;

  & >>> .v-input__slot {
    box-shadow: none;
  }

  & >>> input::placeholder {
    color: #bbb;
  }
}

.thumbnail-container {
  padding: 24px;
}

.thumbnail {
  width: 500px;
  border: 1px solid #ccc;

  &.selectable {
    cursor: pointer;

    &:hover {
      border-color: var(--blue-500);
      box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);
    }

    &.selected {
      border-width: 3px;
      border-color: var(--blue-700);
    }
  }
}

.pages {
  font-size: 13px;
}
</style>
